import FilterDrawer from 'components/Assignments/FilterDrawer';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import { PageContext } from 'pages/InstructorAssignments/PageProvider';
import Desktop from 'pages/InstructorAssignments/desktop';
import Mobile from 'pages/InstructorAssignments/mobile';
import Tablet from 'pages/InstructorAssignments/tablet';
import { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import assignmentService from 'services/assignment';
import courseService from 'services/course';
import { Button, Loading, Stack, Text } from 'shell/components';
import { getDuplicateTitle, isAssignmentValid } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const PageLayout = () => {
    const { isLoading, course, setAssignments } = useApp();
    const { auth, currentUser } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { joyrideRun, setJoyrideRun, setJoyridePaused, joyrideStepIndex, setJoyrideStepIndex, performNext } =
        useJoyride();
    const { message } = useSnackbar();
    const { device } = useViewport();

    const { filters, setFilters, resetFilters } = useContext(PageContext);

    const getFilterCount = (filters) => {
        let count = 0;
        if (filters.sort !== 'none') count++;
        return count;
    };

    const onClickCreate = () => {
        if (joyrideRun) return performNext();
        history.push('/assignments/new');
    };

    const onClick = ({ id }) => {
        if (joyrideRun) {
            if (joyrideStepIndex === 11) {
                history.push(`/assignments/${id}`);
                setJoyrideRun(false);
                return;
            }
            return performNext();
        }
        history.push(`/assignments/${id}`);
    };

    const onClickEdit = ({ id }) => {
        history.push(`/assignments/${id}/edit`);
    };

    const onClickDuplicate = async (assignment) => {
        const timestamp = new Date().getTime();
        const duplicate = {
            ...omit(assignment, ['_id', 'batchCount', 'submissionCount', 'is_rag_ready']),
            id: uuid(),
            owner_account_id: auth.id,
            created_on: timestamp,
            modified_on: timestamp,
            title: getDuplicateTitle(assignment.title),
            is_draft: true,
        };
        setAssignments((current) => [...current, duplicate]);
        message.success(
            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                <Text color={'inherit'} variant={'p1'}>
                    Your assignment was successfully duplicated.
                </Text>
                <Button
                    color={'neutral'}
                    size={'large'}
                    variant={'borderless'}
                    onClick={() => history.push(`/assignments/${duplicate.id}`)}
                >
                    Open
                </Button>
            </Stack>
        );
        await courseService.createCourseAssignment(course.id, duplicate);
    };

    const onClickUnpublish = (assignment) => {
        setDialog({
            open: true,
            message: 'Unpublish assignment?',
            description: 'Are you sure you want to unpublish this assignment?',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Unpublish',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                const timestamp = new Date().getTime();
                setAssignments((current) => {
                    return current.map((currentAssignment) => {
                        if (currentAssignment.id !== assignment.id) return currentAssignment;
                        return {
                            ...currentAssignment,
                            modified_on: timestamp,
                            is_draft: true,
                        };
                    });
                });
                closeDialog();
                message.success('Your assignment was successfully updated.');
                await assignmentService.updateAssignment(assignment.id, {
                    ...omit(assignment, ['_id']),
                    modified_on: timestamp,
                    is_draft: true,
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickPublish = (assignment) => {
        if (!isAssignmentValid(assignment)) {
            return setDialog({
                open: true,
                message: 'Required Information Incomplete',
                description:
                    'Before you can publish this assignment, please edit it and add any missing required information.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Ok',
                cancelButtonText: '',
                onAction: () => closeDialog(),
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }

        setDialog({
            open: true,
            message: 'Publish assignment?',
            description: 'Are you sure you want to publish this assignment?',
            note: '',
            variant: 'primary',
            actionButtonText: 'Publish',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                const timestamp = new Date().getTime();
                setAssignments((current) => {
                    return current.map((currentAssignment) => {
                        if (currentAssignment.id !== assignment.id) return currentAssignment;
                        return {
                            ...currentAssignment,
                            modified_on: timestamp,
                            is_draft: false,
                        };
                    });
                });
                closeDialog();
                message.success('Your assignment was successfully updated.');
                await assignmentService.updateAssignment(assignment.id, {
                    ...omit(assignment, ['_id']),
                    modified_on: timestamp,
                    is_draft: false,
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickDelete = (assignment) => {
        setDialog({
            open: true,
            message: 'Delete Assignment?',
            description: 'This action cannot be undone.',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                setAssignments((current) => current.filter(({ id }) => id !== assignment.id));
                closeDialog();
                message.success('Your assignment was successfully deleted.');
                await assignmentService.deleteAssignment(assignment.id);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    useEffect(() => {
        if (currentUser?.finished_onboarding || isLoading) return;
        if (joyrideStepIndex < 2) {
            setJoyrideStepIndex(2);
            setJoyrideRun(true);
        }
    }, [currentUser?.finished_onboarding, isLoading, joyrideStepIndex, setJoyrideRun, setJoyrideStepIndex]);

    useEffect(() => {
        setJoyridePaused(false);
        return () => setJoyridePaused(true);
    }, [setJoyridePaused]);

    const props = {
        filters,
        setFilters,
        getFilterCount,
        onClickCreate,
        onClick,
        onClickEdit,
        onClickDuplicate,
        onClickUnpublish,
        onClickPublish,
        onClickDelete,
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    return isLoading ? (
        <Loading />
    ) : (
        <>
            {renderResponsiveLayout(device)}
            <FilterDrawer filters={filters} setFilters={setFilters} resetFilters={resetFilters} />
        </>
    );
};

export default PageLayout;
